import React, { useState } from 'react';
import { AlertCircle, X } from 'lucide-react';
import './AccountDeletionModal.css';

interface AccountDeletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string, additionalComments: string) => Promise<void>;
  isLoading?: boolean;
}

const deletionReasons = [
  { value: 'privacy-concerns', label: 'Privacy concerns' },
  { value: 'not-using-service', label: 'Not using the service' },
  { value: 'found-alternative', label: 'Found an alternative' },
  { value: 'too-many-notifications', label: 'Too many notifications' },
  { value: 'data-concerns', label: 'Data privacy concerns' },
  { value: 'other', label: 'Other reason' }
];

const AccountDeletionModal: React.FC<AccountDeletionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false
}) => {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [additionalComments, setAdditionalComments] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!selectedReason) {
      setError('Please select a reason for deletion');
      return;
    }

    try {
      await onSubmit(selectedReason, additionalComments);
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSelectedReason('');
        setAdditionalComments('');
        setSuccess(false);
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to submit deletion request');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="account-deletion-modal-overlay">
      <div className="account-deletion-modal">
        <div className="modal-header">
          <h2>Request Account Deletion</h2>
          <button
            onClick={onClose}
            className="close-button"
            disabled={isLoading}
          >
            <X size={24} />
          </button>
        </div>

        {success ? (
          <div className="modal-content">
            <div className="success-message">
              <div className="success-icon">✓</div>
              <h3>Request Submitted Successfully</h3>
              <p>
                Your account deletion request has been received. Your account will be permanently
                deleted in 30 days. You can cancel this request from your privacy settings at any time.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="deletion-form">
            <div className="modal-content">
              {/* Warning Box */}
              <div className="warning-box">
                <AlertCircle size={20} />
                <div>
                  <p className="warning-title">This action cannot be undone</p>
                  <p className="warning-text">
                    Your account and all associated data will be permanently deleted after 30 days.
                    You can cancel this request anytime before deletion is finalized.
                  </p>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="error-message">
                  <AlertCircle size={18} />
                  <p>{error}</p>
                </div>
              )}

              {/* Reason Selection */}
              <div className="form-group">
                <label className="form-label">
                  Why are you deleting your account? <span className="required">*</span>
                </label>
                <div className="reasons-list">
                  {deletionReasons.map((reason) => (
                    <label key={reason.value} className="reason-option">
                      <input
                        type="radio"
                        name="deletion_reason"
                        value={reason.value}
                        checked={selectedReason === reason.value}
                        onChange={(e) => setSelectedReason(e.target.value)}
                        disabled={isLoading}
                      />
                      <span className="reason-label">{reason.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Additional Comments */}
              <div className="form-group">
                <label className="form-label">
                  Additional feedback (optional)
                </label>
                <textarea
                  value={additionalComments}
                  onChange={(e) => setAdditionalComments(e.target.value)}
                  placeholder="Help us understand how we can improve..."
                  maxLength={500}
                  disabled={isLoading}
                  rows={4}
                  className="textarea-input"
                />
                <p className="char-count">
                  {additionalComments.length}/500
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer">
              <button
                type="button"
                onClick={onClose}
                className="btn-cancel"
                disabled={isLoading}
              >
                Keep My Account
              </button>
              <button
                type="submit"
                className="btn-delete"
                disabled={isLoading || !selectedReason}
              >
                {isLoading ? 'Submitting...' : 'Request Deletion'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AccountDeletionModal;
